import React, {useEffect, useState} from "react";
import SaveIcon from "@mui/icons-material/Save";
import {Button, DialogActions, DialogContent, FormControl, Grid, IconButton, Tooltip} from "@mui/material";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import AlertDialog from "../alert-dialog";
import CustomDialog from "../custom-dialog";
import CustomDialogTitle from "../custom-dialog-title";
import I18nTextField from "../i18n-text-field";
import {hideAiAssistantViewError, submitAiAssistantView} from "../../state/ai-assistant/aiAssistantAction";
import {aiAssistantSelector} from "../../state/ai-assistant/aiAssistantSelector";
import {languageSelector} from "../../state/app/appSelectors";
import {nodeIdSelector} from "../../state/node/nodeSelectors";
import {userSelector} from "../../state/user/userSelectors";
import {validateI18nObj} from "../../utils/i18n";
import {canSaveAsView} from "../../utils/permissions";

const AiSaveView = ({data, viewerConfigs}) => {
  const {t} = useTranslation();
  const dispatch = useDispatch();

  const user = useSelector(userSelector);
  const nodeId = useSelector(nodeIdSelector);
  const language = useSelector(languageSelector);
  const {isViewErrorVisible, viewErrorMessage} = useSelector(aiAssistantSelector);

  const [isSaveViewVisible, setSaveViewVisibility] = useState(false);

  const [view, setView] = useState({});

  const showSaveView = () => setSaveViewVisibility(true);
  const hideSaveView = () => setSaveViewVisibility(false);

  const hideViewErrorAlert = () => dispatch(hideAiAssistantViewError());

  useEffect(() => {
    const layouts = {
      type: data.type,
      viewer: data.viewer,
      title: data.title,
      baseurl: data.baseurl,
      datasource: data.datasource,
      mainentityset: data.mainentityset,
      packageId: data.packageId,
      resourceId: data.resourceId,
      query: data.query,
      initialTableState: viewerConfigs?.tableState,
      initialTableSettings: viewerConfigs?.tableSettings,
      initialChartSettings: viewerConfigs?.chartSettings,
      initialMapSettings: viewerConfigs?.mapSettings,
      config: {...data.config, enableAutoSecondaryDim: true}
    };

    setView({
      type: "View",
      mode: "ODATA_QUERY",
      title: {[language]: data?.title},
      resourceId: data?.resourceId,
      packageId: data?.packageId,
      layouts: JSON.stringify(layouts),
      nodeId: nodeId
    });
  }, [data, viewerConfigs, nodeId, language]);

  return (
    canSaveAsView(user) && (
      <>
        <Grid className="ai-assistant__save-view">
          <Tooltip title={t("modules.aiAssistant.saveView.label")} placement="bottom">
            <IconButton onClick={showSaveView}>
              <SaveIcon />
            </IconButton>
          </Tooltip>
        </Grid>
        <CustomDialog className="ai-assistant__save-view-dialog" open={isSaveViewVisible} onClose={hideSaveView}>
          <CustomDialogTitle onClose={hideSaveView}>
            {t("modules.aiAssistant.saveView.dialogs.save.title")}
          </CustomDialogTitle>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid size={12}>
                <FormControl className="ai-assistant__save-view-title" fullWidth>
                  <I18nTextField
                    label={t("modules.aiAssistant.saveView.dialogs.save.fields.title.label")}
                    required
                    variant="outlined"
                    value={view?.title || ""}
                    onChange={value => setView({...view, title: value})}
                  />
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button classname="ai-assistant__save-view-close-btn" onClick={hideSaveView}>
              {t("commons.confirm.cancel")}
            </Button>
            <Button
              autoFocus
              classname="ai-assistant__save-view-save-btn"
              color="primary"
              onClick={() => {
                dispatch(submitAiAssistantView(nodeId, view));
                hideSaveView();
              }}
              disabled={!validateI18nObj(view.title)}
            >
              {t("commons.confirm.save")}
            </Button>
          </DialogActions>
        </CustomDialog>

        <AlertDialog
          classname="ai-assistant__save-view-duplicate-error-dialog"
          open={isViewErrorVisible}
          onClose={hideViewErrorAlert}
        >
          <CustomDialogTitle onClose={hideViewErrorAlert}>
            {t("modules.aiAssistant.saveView.dialogs.error.title")}
          </CustomDialogTitle>
          <DialogContent>
            {viewErrorMessage && (
              <>
                {t("modules.aiAssistant.saveView.dialogs.error.content") + ": "}
                <b>{Object.keys(viewErrorMessage).join(", ")}</b>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button classname="ai-assistant__save-view-duplicate-error-dialog-close-btn" onClick={hideViewErrorAlert}>
              {t("commons.confirm.close")}
            </Button>
          </DialogActions>
        </AlertDialog>
      </>
    )
  );
};

export default AiSaveView;
